import * as XLSX from 'xlsx';

// Colunas obrigatórias
const REQUIRED_COLUMNS = ['sap_code', 'branch_id', 'layout'];

// Colunas opcionais reconhecidas
const OPTIONAL_COLUMNS = ['product_id'];

const ALL_COLUMNS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];

// ─── helpers ────────────────────────────────────────────────────────────────

const normalizeColumnName = (value) => {
    const text = `${value ?? ''}`
        .replace(/^\uFEFF/, '')
        .trim()
        .toLowerCase();

    const noAccents = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return noAccents
        .replace(/\s+/g, '_')
        .replace(/-+/g, '_')
        .replace(/[^a-z0-9_]/g, '')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
};

const readFirstSheet = (arrayBuffer) => {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook.SheetNames[0] ?? '';
    const sheet = workbook.Sheets[sheetName] ?? {};
    return { sheet, sheetName };
};

const sheetToRows = (sheet) => {
    return XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
};

// ─── pré-check (rápido, sem validação linha a linha) ─────────────────────────

export const buildPrecheckFromArrayBuffer = ({
    arrayBuffer,
    previewRowCount = 3,
}) => {
    const { sheet, sheetName } = readFirstSheet(arrayBuffer);
    const rows = sheetToRows(sheet);

    if (rows.length === 0) {
        return {
            sheetName,
            headers: [],
            rows: [],
            totalRows: 0,
            missingColumns: REQUIRED_COLUMNS,
        };
    }

    const rawHeaders = rows[0].map(String);
    const normalizedHeaders = rawHeaders.map(normalizeColumnName);

    const missingColumns = REQUIRED_COLUMNS.filter(
        (col) => !normalizedHeaders.includes(col)
    );

    const dataRows = rows
        .slice(1)
        .filter((row) => row.some((cell) => `${cell ?? ''}`.trim() !== ''));

    const totalRows = dataRows.length;

    const previewRows = dataRows
        .slice(0, previewRowCount)
        .map((row) => rawHeaders.map((_, idx) => `${row[idx] ?? ''}`));

    return {
        sheetName,
        headers: rawHeaders,
        rows: previewRows,
        totalRows,
        missingColumns,
    };
};

// ─── parse completo ──────────────────────────────────────────────────────────

export const parseProductsFromArrayBuffer = ({ arrayBuffer }) => {
    const { sheet, sheetName } = readFirstSheet(arrayBuffer);
    const rows = sheetToRows(sheet);

    if (rows.length === 0) {
        return { records: [], sheetName };
    }

    const rawHeaders = rows[0].map(String);
    const normalizedHeaders = rawHeaders.map(normalizeColumnName);

    const colIndex = (colName) => normalizedHeaders.indexOf(colName);

    const dataRows = rows
        .slice(1)
        .filter((row) => row.some((cell) => `${cell ?? ''}`.trim() !== ''));

    const records = dataRows.map((row, i) => {
        const get = (col) => {
            const idx = colIndex(col);
            return idx !== -1 ? row[idx] : undefined;
        };

        const productIdRaw = get('product_id');
        const productId =
            productIdRaw === '' || productIdRaw === undefined || productIdRaw === null
                ? null
                : Number(productIdRaw);

        return {
            __rowNumber: i + 2, // linha real na planilha (1-indexed + cabeçalho)
            sap_code: `${get('sap_code') ?? ''}`.trim(),
            branch_id: Number(get('branch_id')),
            layout: `${get('layout') ?? ''}`.trim(),
            product_id: Number.isNaN(productId) ? null : productId,
        };
    });

    return { records, sheetName };
};

// ─── validação linha a linha ─────────────────────────────────────────────────

export const validateRecordsForUpload = async ({
    records,
    maxErrors = 20,
    onProgress,
}) => {
    const errors = [];

    for (let i = 0; i < records.length; i++) {
        const record = records[i];

        onProgress?.({ current: i + 1, total: records.length });

        // yield para não travar a UI
        if (i % 50 === 0) {
            await new Promise((resolve) => setTimeout(resolve, 0));
        }

        if (errors.length >= maxErrors) break;

        if (!record.sap_code) {
            errors.push({
                row: record.__rowNumber,
                field: 'sap_code',
                message: 'sap_code não pode ser vazio',
            });
        }

        if (!record.branch_id || Number.isNaN(record.branch_id)) {
            errors.push({
                row: record.__rowNumber,
                field: 'branch_id',
                message: 'branch_id deve ser um número válido',
            });
        }

        if (!record.layout) {
            errors.push({
                row: record.__rowNumber,
                field: 'layout',
                message: 'layout não pode ser vazio',
            });
        }

        if (
            record.product_id !== null &&
            (Number.isNaN(record.product_id) || record.product_id <= 0)
        ) {
            errors.push({
                row: record.__rowNumber,
                field: 'product_id',
                message: 'product_id deve ser um número positivo ou vazio',
            });
        }
    }

    if (errors.length > 0) {
        const lines = errors
            .map((e) => `Linha ${e.row} [${e.field}]: ${e.message}`)
            .join('\n');
        throw new Error(`Erros encontrados na planilha:\n${lines}`);
    }
};
